import { useState } from "react";
import { ArrowRight, CheckCircle2, Globe2, Sparkles, Building2, MapPin, Phone, Mail, Ruler, DollarSign, Images, ShieldCheck, Users, Briefcase } from "lucide-react";
import { motion } from "framer-motion";

/**
 * ExpoStand Contractor Landing Page
 * - Production-ready single-file React component
 * - Tailwind CSS utility classes
 * - Green color theme for contractors
 * - Minimal dependencies: framer-motion, lucide-react (icons)
 */

const cities = [
  { name: "Las Vegas, USA", shows: "CES, PACK EXPO, MAGIC", img: "https://images.unsplash.com/photo-1605833893615-090a15266ac0?q=80&w=1600&auto=format&fit=crop" },
  { name: "Orlando, USA", shows: "IAAPA, HIMSS, InfoComm", img: "https://images.unsplash.com/photo-1477414348463-c0eb7f1359b6?q=80&w=1600&auto=format&fit=crop" },
  { name: "Chicago, USA", shows: "RSNA, IMTS, FABTECH", img: "https://images.unsplash.com/photo-1494522358652-f30e61a60313?q=80&w=1600&auto=format&fit=crop" },
  { name: "Dubai, UAE", shows: "Gulfood, Arab Health, GITEX", img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=1600&auto=format&fit=crop" },
  { name: "Munich, Germany", shows: "BAU, IAA Mobility, IFAT", img: "https://images.unsplash.com/photo-1568731071750-39ac4d52452b?q=80&w=1600&auto=format&fit=crop" },
  { name: "Shanghai, China", shows: "CIIE, Auto Shanghai, Intertextile", img: "https://images.unsplash.com/photo-1545919947-b7455eccfe32?q=80&w=1600&auto=format&fit=crop" },
];

const gallery = [
  { title: "Custom Booth Build — Las Vegas", img: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1600&auto=format&fit=crop" },
  { title: "Corporate Stand — Dubai", img: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=1600&auto=format&fit=crop" },
  { title: "Modular System — Munich", img: "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1600&auto=format&fit=crop" },
  { title: "Interactive Display — Chicago", img: "https://images.unsplash.com/photo-1529336953121-adb2defc4f79?q=80&w=1600&auto=format&fit=crop" },
  { title: "Premium Installation — Orlando", img: "https://images.unsplash.com/photo-1484704849700-f032a568e944?q=80&w=1600&auto=format&fit=crop" },
  { title: "Brand Activation — Shanghai", img: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1600&auto=format&fit=crop" },
];

export default function ContractorLanding() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    speciality: "",
    experience: "",
    location: "",
    message: "",
    consent: false,
  });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* HEADER */}
      <header className="bg-white px-4 md:px-6 py-4 relative z-50">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" 
              alt="EXPO STAND Logo" 
              className="w-10 h-8 md:w-12 md:h-10" 
            />
            <div className="text-slate-700">
              <div className="text-lg md:text-xl font-semibold leading-tight">EXPO</div>
              <div className="text-lg md:text-xl font-semibold leading-tight">STAND</div>
            </div>
          </a>
          
          {/* Navigation - hidden on mobile */}
          <div className="hidden md:flex items-center space-x-6 text-slate-700 text-sm">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>hello@expostand.com</span>
            </div>
            <div className="relative group">
              <span className="hover:text-green-600 cursor-pointer flex items-center">Sign In <span className="ml-1">▾</span></span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                <a href="/exhibitor/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Exhibitor</a>
                <a href="/contractor/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Contractor</a>
                <a href="/supplier/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Supplier</a>
                <div className="border-t border-gray-100 my-1" />
                <a href="/admin-login" className="block px-4 py-2 text-sm text-red-600 font-medium hover:bg-red-50">Admin Login</a>
              </div>
            </div>
            <div className="relative group">
              <span className="hover:text-green-600 cursor-pointer flex items-center">Sign Up <span className="ml-1">▾</span></span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                <a href="/exhibitor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Exhibitor</a>
                <a href="/contractor/signup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Contractor</a>
                <a href="/supplier/signup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Supplier</a>
              </div>
            </div>
            <span className="hover:text-green-600 cursor-pointer">Contact</span>
          </div>

          {/* Mobile Sign In */}
          <a href="/contractor/login" className="md:hidden px-3 py-1.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors">
            Sign In
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden flex items-center" style={{ height: 'calc(100vh - 80px)' }}>
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2400&auto=format&fit=crop"
            alt="Expo hall background"
            className="h-full w-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/70 to-white" />
        </div>

        <div className="mx-auto max-w-7xl px-6 w-full pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm mb-2">
                <Sparkles className="h-4 w-4" />
                <span>Trusted by leading contractors in 46 countries</span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl leading-tight -mt-2">
                Welcome to the<br />
                <span className="text-green-600">ExpoStand</span><br />
                Contractor Portal
              </h1>
              <p className="mt-4 text-lg leading-7 text-slate-600">
                Your gateway to qualified exhibition projects worldwide. Connect with serious exhibitors, bid on verified projects, and grow your business with our secure platform—no more wasted time on unqualified leads.
              </p>

              <div className="mt-6 space-y-2">
                <p className="text-sm text-slate-700">
                  <strong>🎯 Qualified Leads</strong> - Connect with serious exhibitors who have budget and timeline
                </p>
                <p className="text-sm text-slate-700">
                  <strong>📋 Clear Briefs</strong> - Receive detailed project specifications and requirements
                </p>
                <p className="text-sm text-slate-700">
                  <strong>🌍 Global Reach</strong> - Access projects in major exhibition cities worldwide
                </p>
                <p className="text-sm text-slate-700">
                  <strong>💰 Secure Payments</strong> - Optional escrow protection for milestone-based payments
                </p>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="/contractor/signup"
                  className="inline-flex items-center gap-2 rounded-2xl bg-green-600 px-5 py-3 text-white shadow hover:bg-green-700 transition-colors"
                >
                  Join Network <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="/contractor/discover"
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-slate-900 shadow-sm hover:bg-slate-50 transition-colors"
                >
                  Discover Projects <Briefcase className="h-4 w-4" />
                </a>
              </div>

              <ul className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600"/> Verified projects</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600"/> Fair competition</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600"/> Secure payments</li>
              </ul>
            </motion.div>

            {/* Right side image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=600&auto=format&fit=crop"
                  alt="Professional contractor working on exhibition stand"
                  className="w-full h-[300px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-green-900/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-800">
                      Professional contractors delivering excellence worldwide
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Floating stats cards */}
              <div className="absolute -top-2 -right-2 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">500+</div>
                  <div className="text-xs text-slate-500">Projects</div>
                </div>
              </div>
              
              <div className="absolute top-1/2 -left-2 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">95%</div>
                  <div className="text-xs text-slate-500">Success Rate</div>
                </div>
              </div>
              
              <div className="absolute -bottom-2 left-1/4 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-600">46</div>
                  <div className="text-xs text-slate-500">Countries</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-green-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight">Grow Your Business—Here's How</h2>
            <p className="mt-2 text-slate-600 mb-4">Three simple steps from registration to project completion.</p>
            <p className="text-slate-700">
              Stop chasing unqualified leads and unclear project requirements. Our platform connects you with serious exhibitors 
              who have defined budgets, clear timelines, and detailed specifications. 
              <span className="font-medium text-green-600"> Average project value increased by 25% | 40% faster project acquisition.</span>
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Join Our Network",
                desc: "Complete your contractor profile with portfolio, certifications, and service areas. Get verified and start receiving qualified project invitations immediately.",
                Icon: Users,
                badge: "Step 1",
              },
              {
                title: "Receive & Bid Projects",
                desc: "Get notified of relevant projects in your expertise area. Review detailed briefs and submit competitive proposals with clear pricing and timelines.",
                Icon: Briefcase,
                badge: "Step 2",
              },
              {
                title: "Deliver & Get Paid",
                desc: "Win projects, execute with confidence, and receive secure payments through our escrow system. Build your reputation and unlock premium opportunities.",
                Icon: CheckCircle2,
                badge: "Step 3",
              },
            ].map(({ title, desc, Icon, badge }) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-green-50" />
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">{badge}</div>
                <Icon className="mb-4 h-8 w-8" />
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="mt-2 text-slate-600">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-12 md:py-16">
          <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">Where We Connect You</h2>
              <p className="mt-2 text-slate-600">Active contractor network in <span className="font-semibold text-slate-900">46 countries</span>—expanding every week.</p>
            </div>
            <a href="/contractor/locations" className="inline-flex items-center gap-2 text-green-700 hover:underline">
              See all opportunities <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
            {cities.map((c) => (
              <div key={c.name} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="relative h-40 w-full overflow-hidden">
                  <img src={c.img} alt={c.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold">{c.name}</h3>
                  <p className="mt-1 text-sm text-slate-600">{c.shows}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <div className="mb-10 max-w-3xl">
          <h2 className="text-3xl font-semibold tracking-tight">Membership Plans</h2>
          <p className="mt-2 text-slate-600">Choose the plan that fits your contracting business goals.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[{
            name: "Basic",
            price: "$0",
            period: "/month",
            features: ["Access to public project listings", "Basic portfolio showcase", "Standard messaging", "Community support"],
            cta: "Get Started",
          },{
            name: "Professional",
            price: "$199",
            period: "/month",
            features: ["Priority project invitations", "Advanced portfolio tools", "Direct client messaging", "Escrow payment protection", "Premium support"],
            cta: "Start Free Trial",
            highlighted: true,
          },{
            name: "Enterprise",
            price: "Custom",
            period: "",
            features: ["Exclusive project access", "Multi-team management", "Custom contract templates", "Dedicated account manager", "API integrations"],
            cta: "Contact Sales",
          }].map((p) => (
            <div key={p.name} className={`flex h-full flex-col rounded-2xl border bg-white p-6 shadow-sm ${p.highlighted ? "border-green-600 ring-2 ring-green-100" : "border-slate-200"}`}>
              <div className="mb-4 flex items-baseline justify-between">
                <h3 className="text-xl font-semibold">{p.name}</h3>
                {p.highlighted && <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">Popular</span>}
              </div>
              <div className="mb-4">
                <span className="text-4xl font-bold">{p.price}</span>
                <span className="text-slate-600">{p.period}</span>
              </div>
              <ul className="mb-6 space-y-2 text-slate-700">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600"/> <span>{f}</span></li>
                ))}
              </ul>
              <a href={p.name === "Enterprise" ? "#/contact" : "#/contractor/signup"} className={`mt-auto inline-flex items-center justify-center rounded-2xl px-4 py-2 font-medium shadow-sm ${p.highlighted ? "bg-green-600 text-white hover:bg-green-700" : "bg-slate-900 text-white hover:bg-slate-800"}`}>{p.cta}</a>
              <p className="mt-3 text-xs text-slate-500">Success fee applies only to completed projects.</p>
            </div>
          ))}
        </div>
      </section>

      {/* CALLBACK FORM */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-12 md:py-16">
          <div className="mb-8 max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight">Ready to Grow Your Business? Let's Connect.</h2>
            <p className="mt-2 text-slate-600">Tell us about your contracting business and we'll get you set up within 24 hours.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <form className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Full Name" placeholder="e.g., John Smith" value={form.name} onChange={(v) => update("name", v)} Icon={Building2} />
                <Field label="Work Email" placeholder="name@company.com" type="email" value={form.email} onChange={(v) => update("email", v)} Icon={Mail} />
                <Field label="Company Name" placeholder="Your contracting business" value={form.company} onChange={(v) => update("company", v)} Icon={Building2} />
                <Field label="Speciality" placeholder="e.g., Custom builds, AV, Graphics" value={form.speciality} onChange={(v) => update("speciality", v)} Icon={Briefcase} />
                <Field label="Years Experience" placeholder="e.g., 5+ years" value={form.experience} onChange={(v) => update("experience", v)} Icon={Users} />
                <Field label="Primary Location" placeholder="e.g., Dubai, Munich" value={form.location} onChange={(v) => update("location", v)} Icon={MapPin} />
              </div>
              <div className="mt-4">
                <Label>Tell us about your business</Label>
                <textarea
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm outline-none ring-offset-background placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-green-600"
                  rows={4}
                  placeholder="What services do you offer? What's your typical project size?"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                />
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} />
                I'm interested in joining the contractor network.
              </label>
              <button type="button" className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-green-600 px-5 py-3 font-medium text-white shadow hover:bg-green-700">
                Request Partnership <Phone className="h-4 w-4" />
              </button>
            </form>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">What we need to verify your business</h3>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-700">
                <li>Business registration and certifications</li>
                <li>Portfolio of recent exhibition projects</li>
                <li>Client references and testimonials</li>
                <li>Insurance and safety compliance documents</li>
                <li>Service areas and capacity details</li>
                <li>Preferred project types and size range</li>
              </ul>
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <p className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-5 w-5"/> All contractors undergo thorough verification including background checks, insurance validation, and portfolio review before joining our network.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSPIRING DESIGNS */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-12 md:py-16">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">Showcase Your Best Work</h2>
              <p className="mt-2 text-slate-600">Join contractors creating award-winning exhibition experiences worldwide.</p>
            </div>
            <a href="/contractor/portfolio" className="inline-flex items-center gap-2 text-green-700 hover:underline">
              Upload your portfolio <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
            {[
              { title: "Modern Interactive Stand", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/1p6p52h6_behance_download_1512683025775.jpg" },
              { title: "Elegant Corporate Display", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/og4pi04c_d99fbd54082151.594c269fa2921.jpg" },
              { title: "Creative Brand Experience", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/wly6v2kk_behance_download_1512846870389.jpg" },
              { title: "Professional Presentation Zone", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/elbz7932_Screen%20Shot%202018-10-28%20at%2007.16.31.png" },
              { title: "Innovative Product Showcase", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/1p6p52h6_behance_download_1512683025775.jpg" },
              { title: "Premium Brand Activation", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/og4pi04c_d99fbd54082151.594c269fa2921.jpg" },
            ].map((design) => (
              <div key={design.title} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="relative h-44 w-full overflow-hidden">
                  <img src={design.img} alt={design.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-slate-900">{design.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Success Stories From Our Network</h2>
            <p className="mt-2 text-slate-600">Real projects delivered by verified contractors—worldwide impact.</p>
          </div>
          <a href="/contractor/success-stories" className="inline-flex items-center gap-2 text-green-700 hover:underline">View success stories <ArrowRight className="h-4 w-4"/></a>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
          {gallery.map((g) => (
            <div key={g.title} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="relative h-44 w-full overflow-hidden">
                <img src={g.img} alt={g.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </div>
              <div className="p-4">
                <p className="text-sm font-medium text-slate-900">{g.title}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <div className="grid gap-8 md:grid-cols-5">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 text-xl font-bold"><Images className="h-6 w-6"/> ExpoStand</div>
              <p className="mt-2 max-w-md text-sm text-slate-600">Connecting contractors with quality exhibition projects, worldwide.</p>
              <p className="mt-4 text-xs text-slate-500">© 2025 ExpoStand. All rights reserved.</p>
            </div>
            <nav className="grid grid-cols-2 gap-8 md:col-span-3 md:grid-cols-3">
              <div>
                <h4 className="text-sm font-semibold">For Contractors</h4>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li><a href="/contractor/signup" className="hover:underline">Join Network</a></li>
                  <li><a href="/contractor/projects" className="hover:underline">Browse Projects</a></li>
                  <li><a href="/contractor/success-stories" className="hover:underline">Success Stories</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold">Resources</h4>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li><a href="/contractor/guide" className="hover:underline">Contractor Guide</a></li>
                  <li><a href="/pricing" className="hover:underline">Pricing</a></li>
                  <li><a href="/support" className="hover:underline">Support</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold">Company</h4>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li><a href="/about" className="hover:underline">About</a></li>
                  <li><a href="/legal/terms" className="hover:underline">Terms</a></li>
                  <li><a href="/legal/privacy" className="hover:underline">Privacy</a></li>
                </ul>
              </div>
            </nav>
          </div>
          <p className="mt-6 text-xs text-slate-500">ExpoStand connects verified contractors with qualified exhibition projects. All partnerships subject to verification and quality standards.</p>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, placeholder, value, onChange, type = "text", Icon }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-green-600">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        <input
          type={type}
          className="w-full bg-transparent outline-none placeholder:text-slate-400"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

function Label({ children }) {
  return <label className="text-sm font-medium text-slate-700">{children}</label>;
}